import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    FaRegBell,
    FaBroadcastTower,
    FaPaperPlane,
    FaCheckDouble,
    FaEye,
    FaPlus,
    FaSearch,
    FaInbox,
    FaBullhorn,
    FaUserGraduate,
    FaHistory,
    FaTimes,
    FaChevronRight,
    FaExclamationTriangle,
    FaInfoCircle,
    FaCheckCircle,
    FaBolt,
    FaTimesCircle
} from 'react-icons/fa';
import DataTable from '../../SuperAdmin/shared/DataTable';
import Loader from '../../shared/Loader';
import { showSuccess, showError, showLoading, closeLoading } from '../../../utils/Alert';
import './TutorDashboardOverview.css';
import '../../SuperAdmin/views/UserModals.css';

const TutorNotifications = () => {
    const [messages, setMessages] = useState([]);
    const [courses, setCourses] = useState([]);
    const [broadcasts, setBroadcasts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isComposeModalOpen, setIsComposeModalOpen] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [viewTab, setViewTab] = useState('Inbox'); // 'Inbox', 'System', or 'Broadcasts'
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        courseId: '',
        receiverId: '', // For direct replies
        subject: '',
        content: '',
        type: 'Broadcast'
    });

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            const token = localStorage.getItem('token');
            const [msgRes, courseRes, broadcastRes] = await Promise.all([
                axios.get('/api/tutor/messages/inbox', { headers: { Authorization: `Bearer ${token}` } }),
                axios.get('/api/tutor/courses', { headers: { Authorization: `Bearer ${token}` } }),
                axios.get('/api/notifications', { headers: { Authorization: `Bearer ${token}` } })
            ]);
            setMessages(msgRes.data);
            setCourses(courseRes.data.filter(c => c.status === 'Active'));
            setBroadcasts(broadcastRes.data);
            setLoading(false);
        } catch (err) {
            showError("Sync Failure", "Could not synchronize the communication hub.");
            setLoading(false);
        }
    };

    const handleMarkAsRead = async (id) => {
        try {
            const token = localStorage.getItem('token');
            await axios.patch(`/api/tutor/messages/${id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } });
            setMessages(prev => prev.map(m => m._id === id ? { ...m, isRead: true } : m));
        } catch (err) {
            console.error("Read status error", err);
        }
    };

    const handleDismissBroadcast = async (id, e) => {
        if (e) e.stopPropagation();
        try {
            const token = localStorage.getItem('token');
            await axios.patch(`/api/notifications/${id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } });
            setBroadcasts(prev => {
                const updated = prev.filter(b => b._id !== id);
                const newUnread = updated.filter(b => !b.isRead).length;
                window.dispatchEvent(new CustomEvent('notificationsRead', { detail: { count: newUnread } }));
                return updated;
            });
        } catch (err) {
            console.error("Broadcast dismiss error", err);
        }
    };

    const handleNotifClick = (notif) => {
        let targetPath = null;

        // Priority 1: Direct link
        if (notif.link) {
            targetPath = notif.link;
        }
        // Priority 2: Keyword-based mapping
        else {
            const context = `${notif.title} ${notif.message}`.toLowerCase();

            if (context.includes('course') || context.includes('enroll')) {
                targetPath = '/admin/tutor-courses';
            } else if (context.includes('live') || context.includes('session') || context.includes('studio')) {
                targetPath = '/admin/tutor-live-classes';
            } else if (context.includes('attendance') || context.includes('roster') || context.includes('presence')) {
                targetPath = '/admin/tutor-attendance';
            } else if (context.includes('ticket') || context.includes('support') || context.includes('payroll') || context.includes('admin')) {
                targetPath = '/admin/tutor-support';
            } else if (context.includes('assignment') || context.includes('exam') || context.includes('test') || context.includes('grade')) {
                targetPath = '/admin/tutor-assignments';
            } else if (context.includes('performance') || context.includes('metric')) {
                targetPath = '/admin/tutor-students';
            }
        }

        // Action: Mark as read/dismiss regardless of redirection status
        handleDismissBroadcast(notif._id);

        // Redirect if a valid path was determined
        if (targetPath) {
            navigate(targetPath);
        } else {
            // If no specific section, we've already dismissed it (made it disappear)
            showSuccess('Notification Read', 'Communication has been logged and archived.');
        }
    };

    const handleMarkAllRead = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.patch('/api/notifications/mark-all-read', {}, { headers: { Authorization: `Bearer ${token}` } });
            setBroadcasts([]);
            window.dispatchEvent(new CustomEvent('notificationsRead', { detail: { count: 0 } }));
            showSuccess('All Clear', 'All broadcasts have been marked as read.');
        } catch (err) {
            showError('Error', 'Could not mark broadcasts as read.');
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        showLoading('Dispatching Message...');
        try {
            const token = localStorage.getItem('token');
            await axios.post('/api/tutor/messages/send', formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            closeLoading();
            showSuccess('Dispatched', 'Your communication has been securely transmitted.');
            setIsComposeModalOpen(false);
            setFormData({ courseId: '', receiverId: '', subject: '', content: '', type: 'Broadcast' });
        } catch (err) {
            closeLoading();
            showError('Transmission Error', 'Failed to reach the student dashboard.');
        }
    };

    const openReply = (msg) => {
        setSelectedMessage(msg);
        setFormData({
            courseId: msg.courseId?._id || '',
            receiverId: msg.senderId?._id || '',
            subject: `Re: ${msg.subject || 'Student Query'}`,
            content: '',
            type: 'Direct',
            parentMessageId: msg._id
        });
        setIsComposeModalOpen(true);
        if (!msg.isRead) handleMarkAsRead(msg._id);
    };

    const messageColumns = [
        {
            header: 'Sender/Student', key: 'senderId', width: '25%', render: (row) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div className="avatar" style={{ width: '28px', height: '28px', fontSize: '11px' }}>
                        {row.senderId?.name?.charAt(0) || 'S'}
                    </div>
                    <div>
                        <strong style={{ color: '#0F172A', fontSize: '13px' }}>{row.senderId?.name}</strong>
                        <span style={{ fontSize: '10px', color: '#64748B', display: 'block' }}>{row.courseId?.title || 'General'}</span>
                    </div>
                </div>
            )
        },
        {
            header: 'Subject & Preview', key: 'content', width: '40%', render: (row) => (
                <div>
                    <strong style={{ color: row.isRead ? '#64748B' : '#0F172A', fontSize: '13px' }}>{row.subject || 'Enquiry'}</strong>
                    <div style={{ fontSize: '12px', color: '#64748B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.content}
                    </div>
                </div>
            )
        },
        {
            header: 'Timestamp', key: 'createdAt', width: '20%', render: (row) => (
                <div style={{ fontSize: '12px', color: '#64748B' }}>
                    {new Date(row.createdAt).toLocaleDateString()}
                    <div style={{ fontSize: '10px' }}>{new Date(row.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                </div>
            )
        },
        {
            header: 'Actions', key: 'actions', width: '15%', render: (row) => (
                <div className="action-buttons">
                    <button className="action-btn view" title="Read & Reply" onClick={() => openReply(row)}>
                        <FaInbox />
                    </button>
                </div>
            )
        }
    ];

    if (loading) return <Loader />;

    return (
        <div className="dashboard-overview fade-in">
            <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <h1 className="page-title">Unified Communication Hub</h1>
                    <p className="page-subtitle">Centralized inbox for student queries, automated alerts, and batch announcements.</p>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                    <button className="primary-btn pulse-on-hover" onClick={() => {
                        setFormData({ courseId: '', receiverId: '', subject: '', content: '', type: 'Broadcast' });
                        setIsComposeModalOpen(true);
                    }}>
                        <FaPlus style={{ marginRight: '8px' }} /> New Broadcast
                    </button>
                </div>
            </div>

            {/* Hub Switcher Tags */}
            <div style={{ display: 'flex', gap: '24px', borderBottom: '1px solid #E2E8F0', marginBottom: '32px' }}>
                <button
                    onClick={() => setViewTab('Inbox')}
                    style={{
                        padding: '12px 4px', background: 'none', border: 'none',
                        borderBottom: viewTab === 'Inbox' ? '3px solid #002C5F' : '3px solid transparent',
                        color: viewTab === 'Inbox' ? '#002C5F' : '#64748B', fontWeight: 700, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: '8px'
                    }}
                >
                    <FaInbox /> Student Enquiries
                    {messages.filter(m => !m.isRead).length > 0 && (
                        <span style={{ background: '#3B82F6', color: '#fff', borderRadius: '20px', fontSize: '10px', fontWeight: 800, padding: '2px 8px' }}>
                            {messages.filter(m => !m.isRead).length}
                        </span>
                    )}
                </button>
                <button
                    onClick={() => setViewTab('System')}
                    style={{
                        padding: '12px 4px', background: 'none', border: 'none',
                        borderBottom: viewTab === 'System' ? '3px solid #002C5F' : '3px solid transparent',
                        color: viewTab === 'System' ? '#002C5F' : '#64748B', fontWeight: 700, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: '8px'
                    }}
                >
                    <FaRegBell /> System & Audit Alerts
                    {broadcasts.filter(b => !b.isRead && b.type === 'Alert').length > 0 && (
                        <span style={{ background: '#EF4444', color: '#fff', borderRadius: '20px', fontSize: '10px', fontWeight: 800, padding: '2px 8px' }}>
                            {broadcasts.filter(b => !b.isRead && b.type === 'Alert').length}
                        </span>
                    )}
                </button>
                <button
                    onClick={() => setViewTab('Broadcasts')}
                    style={{
                        padding: '12px 4px', background: 'none', border: 'none',
                        borderBottom: viewTab === 'Broadcasts' ? '3px solid var(--primary-gold, #D4AF37)' : '3px solid transparent',
                        color: viewTab === 'Broadcasts' ? 'var(--primary-gold, #D4AF37)' : '#64748B', fontWeight: 700, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: '8px'
                    }}
                >
                    <FaBroadcastTower /> Admin Broadcasts
                    {broadcasts.filter(b => !b.isRead && b.type !== 'Alert').length > 0 && (
                        <span style={{ background: '#EF4444', color: '#fff', borderRadius: '20px', fontSize: '10px', fontWeight: 800, padding: '2px 8px' }}>
                            {broadcasts.filter(b => !b.isRead && b.type !== 'Alert').length}
                        </span>
                    )}
                </button>
            </div>

            {viewTab === 'Inbox' ? (
                <DataTable
                    title="Communication Streams"
                    columns={messageColumns}
                    data={messages}
                    filterOptions={[
                        { label: 'Unread First', value: 'unread' },
                        { label: 'All Conversations', value: '' }
                    ]}
                />
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {/* Mark All Read Header */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                            onClick={handleMarkAllRead}
                            className="action-btn-secondary"
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '12px' }}
                        >
                            <FaCheckDouble size={14} /> Mark All as Read
                        </button>
                    </div>

                    {broadcasts
                        .filter(b => viewTab === 'System' ? b.type === 'Alert' : b.type !== 'Alert')
                        .length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '80px 60px', background: 'white', borderRadius: '24px', border: '1px solid #E2E8F0' }}>
                            <FaRegBell size={48} color="#94A3B8" style={{ marginBottom: '20px' }} />
                            <h3 style={{ color: '#0F172A', fontWeight: 700 }}>{viewTab === 'System' ? 'No System Disruptions' : 'No Broadcasts Received'}</h3>
                            <p style={{ color: '#64748B' }}>{viewTab === 'System' ? 'Platform-wide alerts and mission-critical updates will appear here.' : 'Admin platform-wide announcements will appear here once deployed.'}</p>
                        </div>
                    ) : (
                        broadcasts
                            .filter(b => viewTab === 'System' ? b.type === 'Alert' : b.type !== 'Alert')
                            .map((b, idx) => {
                                const typeConfig = {
                                    Alert: { bg: 'linear-gradient(135deg, #FEF3C7, #FFF7ED)', border: '#F59E0B', badge: '#F59E0B', label: 'ALERT', Icon: FaBolt },
                                    Info: { bg: 'linear-gradient(135deg, #EFF6FF, #F0F9FF)', border: '#3B82F6', badge: '#3B82F6', label: 'INFO', Icon: FaInfoCircle },
                                    Warning: { bg: 'linear-gradient(135deg, #FEF2F2, #FFF1F2)', border: '#EF4444', badge: '#EF4444', label: 'WARNING', Icon: FaExclamationTriangle },
                                    Success: { bg: 'linear-gradient(135deg, #F0FDF4, #ECFDF5)', border: '#10B981', badge: '#10B981', label: 'SUCCESS', Icon: FaCheckCircle },
                                };
                                const cfg = typeConfig[b.type] || typeConfig.Info;
                                const TypeIcon = cfg.Icon;
                                return (
                                    <div
                                        key={b._id || idx}
                                        className="fade-in"
                                        onClick={() => handleNotifClick(b)}
                                        style={{
                                            background: cfg.bg,
                                            borderRadius: '20px',
                                            padding: '28px 36px',
                                            borderLeft: `6px solid ${cfg.border}`,
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                                            display: 'flex',
                                            gap: '24px',
                                            alignItems: 'flex-start',
                                            position: 'relative',
                                            cursor: 'pointer',
                                            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.08)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.04)';
                                        }}
                                    >
                                        {/* Type Icon */}
                                        <div style={{ flexShrink: 0 }}>
                                            <div style={{ width: '52px', height: '52px', background: cfg.border, borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <TypeIcon color="#fff" size={22} />
                                            </div>
                                        </div>

                                        <div style={{ flex: 1 }}>
                                            {/* Top row: type badge + target — no timestamp here */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px', flexWrap: 'wrap', paddingRight: '36px' }}>
                                                <span style={{ background: cfg.badge, color: '#fff', padding: '3px 12px', borderRadius: '20px', fontSize: '10px', fontWeight: 800, letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                    <TypeIcon size={10} /> {cfg.label}
                                                </span>
                                                <span style={{ fontSize: '12px', color: '#64748B', fontWeight: 600 }}>
                                                    TARGET: {(b.target || 'all').toUpperCase()}
                                                </span>
                                            </div>
                                            {/* Timestamp on its own dedicated row */}
                                            <div style={{ fontSize: '11px', color: '#94A3B8', marginBottom: '10px', fontWeight: 500 }}>
                                                {new Date(b.createdAt).toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' })} &bull;{' '}
                                                {new Date(b.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                            <h4 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 700, color: '#0F172A' }}>{b.title}</h4>
                                            <p style={{ margin: 0, fontSize: '14.5px', color: '#334155', lineHeight: '1.7' }}>{b.message}</p>
                                        </div>

                                        {/* Dismiss Button */}
                                        <button
                                            onClick={(e) => handleDismissBroadcast(b._id, e)}
                                            title="Mark as read & dismiss"
                                            style={{
                                                position: 'absolute',
                                                top: '16px',
                                                right: '20px',
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                color: '#94A3B8',
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '4px',
                                                borderRadius: '50%',
                                                transition: 'color 0.2s, background 0.2s'
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.color = '#EF4444'; e.currentTarget.style.background = '#FEE2E2'; }}
                                            onMouseLeave={e => { e.currentTarget.style.color = '#94A3B8'; e.currentTarget.style.background = 'none'; }}
                                        >
                                            <FaTimesCircle size={20} />
                                        </button>
                                    </div>
                                );
                            })
                    )}
                </div>
            )}


            {/* Compose / Reply Modal - Aligned with Live Class Styling */}
            {isComposeModalOpen && createPortal(
                <div className={`modal-overlay ${isComposeModalOpen ? 'open' : ''}`} onClick={() => setIsComposeModalOpen(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
                        <div className="modal-header">
                            <h2>
                                {formData.receiverId ? <FaUserGraduate /> : <FaBullhorn />}
                                {formData.receiverId ? 'Secure Response Center' : 'Cohort Broadcast Hub'}
                            </h2>
                            <button className="modal-close-btn" onClick={() => setIsComposeModalOpen(false)}><FaTimes /></button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={handleSendMessage} className="modal-form">
                                {/* Section 1: Targeting */}
                                <div className="form-group full-width">
                                    <h3 style={{ color: 'var(--primary-blue)', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px', margin: 0 }}>
                                        {formData.receiverId ? '1. Thread Context' : '1. Target Audience Cohort'}
                                    </h3>
                                </div>

                                {!formData.receiverId ? (
                                    <div className="form-group full-width">
                                        <label className="premium-label">Select Group (Batch/Course)</label>
                                        <select
                                            className="glass-input"
                                            value={formData.courseId}
                                            onChange={(e) => setFormData({ ...formData, courseId: e.target.value })}
                                            required
                                        >
                                            <option value="">Select Target Enrollment Group...</option>
                                            {courses.map(c => <option key={c._id} value={c._id}>{c.title}</option>)}
                                        </select>
                                    </div>
                                ) : (
                                    <div className="form-group full-width">
                                        <label className="premium-label">Replying to Student Enquiry</label>
                                        <input type="text" className="glass-input" value={selectedMessage?.senderId?.name || 'Student'} disabled style={{ opacity: 0.7 }} />
                                    </div>
                                )}

                                {/* Section 2: Transmission Content */}
                                <div className="form-group full-width">
                                    <h3 style={{ color: 'var(--primary-blue)', borderBottom: '1px solid var(--border-color)', paddingBottom: '8px', margin: '16px 0 0 0' }}>2. Transmission Briefing</h3>
                                </div>

                                <div className="form-group full-width">
                                    <label className="premium-label">Communication Subject Header</label>
                                    <input
                                        type="text"
                                        className="glass-input"
                                        value={formData.subject}
                                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                        required
                                        placeholder="e.g. Action Required: Module 2 Project Feedback"
                                    />
                                </div>

                                <div className="form-group full-width">
                                    <label className="premium-label">Transmission Content</label>
                                    <textarea
                                        className="glass-input resize-none"
                                        rows="8"
                                        value={formData.content}
                                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                        required
                                        placeholder="Draft your mission-critical communication here..."
                                    />
                                </div>

                                <div className="modal-actions">
                                    <button type="button" className="action-btn-secondary" onClick={() => setIsComposeModalOpen(false)}>Cancel Dispatch</button>
                                    <button type="submit" className="action-btn-gold pulse-on-hover">
                                        {formData.receiverId ? 'Post Secure Reply' : 'Broadcast to Cohort'} <FaChevronRight style={{ marginLeft: '10px' }} />
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default TutorNotifications;
